"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { EventBinding } from "@/lib/api/events";
import {
  ownsEventStorageKey,
  persistEventOperation,
  removeEventOperation,
  restoreEventOperations,
  type EventOperation,
} from "@/lib/commands/event-operation-store";

export function useEventRecovery(binding: EventBinding) {
  const [records, setRecords] = useState<EventOperation[]>([]);
  const [available, setAvailable] = useState(false);
  const [ready, setReady] = useState(false);
  const [terminals, setTerminals] = useState<
    (EventOperation & { terminalState: "Completed" | "Failed" })[]
  >([]);
  const current = useRef(records);
  const sync = useCallback(() => {
    try {
      const next = restoreEventOperations(window.localStorage, binding);
      current.current = next;
      setRecords(next);
      setAvailable(true);
    } catch {
      setAvailable(false);
    } finally {
      setReady(true);
    }
  }, [binding]);
  useEffect(() => {
    const timer = window.setTimeout(sync, 0);
    function changed(event: StorageEvent) {
      if (!event.key || ownsEventStorageKey(binding, event.key)) sync();
    }
    window.addEventListener("storage", changed);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("storage", changed);
    };
  }, [binding, sync]);
  function save(record: EventOperation) {
    try {
      persistEventOperation(window.localStorage, binding, record);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  function remove(key: string) {
    try {
      removeEventOperation(window.localStorage, binding, key);
      sync();
    } catch {
      setAvailable(false);
    }
  }
  return {
    terminals,
    resolve: (
      record: EventOperation,
      terminalState: "Completed" | "Failed",
    ) => {
      remove(record.idempotencyKey);
      setTerminals((before) =>
        [
          ...before.filter(
            (item) => item.idempotencyKey !== record.idempotencyKey,
          ),
          { ...record, terminalState },
        ].slice(-12),
      );
    },
    dismiss: (key: string) =>
      setTerminals((before) =>
        before.filter((item) => item.idempotencyKey !== key),
      ),
    records,
    ready,
    available,
    sync,
    save,
    remove,
    read: () => restoreEventOperations(window.localStorage, binding),
    current,
  };
}
